import React, { useCallback, useEffect, useMemo, useState, type ComponentType } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import {
    SparklesIcon,
    CalendarDaysIcon,
    ArrowPathIcon,
    AdjustmentsHorizontalIcon,
    BuildingOfficeIcon,
    RectangleStackIcon,
    UserGroupIcon,
    UserCircleIcon,
    CurrencyDollarIcon,
    XCircleIcon,
    ArrowUturnLeftIcon,
    CheckCircleIcon,
    TrashIcon,
    BellAlertIcon,
    ExclamationTriangleIcon,
    ChartBarIcon,
    InformationCircleIcon,
    ClockIcon,
    BoltIcon,
    MagnifyingGlassIcon,
    PlusIcon,
} from '@heroicons/react/24/solid';
import {
    AllocationTimelineEvent,
    AllocationTimelineEventSeverity,
    ALLOCATION_TYPE_METADATA,
    ALLOCATION_BUCKET_METADATA,
    AllocationBucketTypes,
} from '@/features/Collaborators/Models';
import { useAllocationTimelineQuery } from '@/features/Collaborators/Allocations/data';
import { CollaboratorCombobox } from '@/components/shared';
import { CollaboratorAvatar } from '@/features/Collaborators/Shared';
import { useCollaboratorsRealtimeQuery } from '@/features/Collaborators/Data/hooks/useCollaboratorsRealtimeQuery';
import type { Collaborator, CollaboratorVacationSummary } from '@/features/Collaborators/Models/collaborator';
import { ALLOCATION_STATUS_METADATA, ALLOCATION_STATUS_ORDER } from '@/features/Collaborators/List/Contexts/StatusManagement/constants/allocationStatus';
import { useAuth, ROLE_ADMIN, ROLE_COLLABORATOR_ADMIN, ROLE_COLLABORATOR_VIEWER, ROLE_COLLABORATOR_EDITOR } from '@/contexts/AuthContext';
import type { AllocationPreviewStatus } from '@/features/Collaborators/Models';
import { buildVacationAllocationLabel } from '@/features/Vacations/utils/vacationAllocationLabel';
import { AllocationTypes } from '@/features/Collaborators/Models';
import TimelineView from './TimelineView';
import ClientsView from './ClientsView';
import { ALLOCATION_TYPE_ICON_MAP } from '@/features/Collaborators/Allocations/constants/allocationTypeIcons';
import { Squares2X2Icon as DefaultTypeIcon } from '@heroicons/react/24/solid';
import { StarIcon } from '@heroicons/react/20/solid';
import { buildClientLogoUrl } from '@/components/ClientLogo';
import { formatCapacityLabel } from '@/features/Collaborators/Allocations/utils/formatCapacityLabel';
import { normalizeString } from '@/utils/stringUtils';

type EventType = AllocationTimelineEvent['type'];
type Severity = AllocationTimelineEventSeverity;

export interface EventMeta {
    label: string;
    icon: React.ElementType;
    tone: string;
}

interface CollaboratorsLayoutContext {
    collaborators: Collaborator[];
    isLoading: boolean;
    error: Error | null;
}

type ViewMode = 'timeline' | 'clients';

const AVATAR_DISPLAY_LIMIT = 10;

interface ScopeCollaborator {
    id: string;
    name: string;
    photoUrl?: string | null;
}

interface ScopeStat {
    name: string;
    field: string;
    stat: string;
    updated: number;
    total: number;
    filterState: 'updated' | 'not_updated' | 'none';
    collaborators: ScopeCollaborator[];
}

interface AllocationScopeStats {
    primary: ScopeStat[];
    absence: ScopeStat;
    notice: {
        name: string;
        count: number;
        collaborators: ScopeCollaborator[];
    };
}

const EVENT_TYPE_META: Record<EventType, EventMeta> = {
    allocation_created: {
        label: 'Criação',
        icon: SparklesIcon,
        tone: 'text-sky-600 bg-sky-50',
    },
    allocation_updated_start: {
        label: 'Início alterado',
        icon: CalendarDaysIcon,
        tone: 'text-indigo-600 bg-indigo-50',
    },
    allocation_updated_end: {
        label: 'Término alterado',
        icon: CalendarDaysIcon,
        tone: 'text-indigo-600 bg-indigo-50',
    },
    allocation_extended: {
        label: 'Prorrogação',
        icon: ArrowPathIcon,
        tone: 'text-emerald-600 bg-emerald-50',
    },
    allocation_dedication_changed: {
        label: 'Dedicação ajustada',
        icon: ChartBarIcon,
        tone: 'text-purple-600 bg-purple-50',
    },
    allocation_type_changed: {
        label: 'Tipo alterado',
        icon: DefaultTypeIcon,
        tone: 'text-amber-600 bg-amber-50',
    },
    allocation_client_changed: {
        label: 'Cliente alterado',
        icon: BuildingOfficeIcon,
        tone: 'text-teal-600 bg-teal-50',
    },
    allocation_project_changed: {
        label: 'Projeto alterado',
        icon: RectangleStackIcon,
        tone: 'text-teal-600 bg-teal-50',
    },
    allocation_responsible_changed: {
        label: 'Responsável alterado',
        icon: UserGroupIcon,
        tone: 'text-fuchsia-600 bg-fuchsia-50',
    },
    allocation_client_responsible_changed: {
        label: 'Cliente responsável alterado',
        icon: UserCircleIcon,
        tone: 'text-fuchsia-600 bg-fuchsia-50',
    },
    allocation_rate_changed: {
        label: 'Taxa alterada',
        icon: CurrencyDollarIcon,
        tone: 'text-emerald-600 bg-emerald-50',
    },
    allocation_cancelled: {
        label: 'Cancelamento',
        icon: XCircleIcon,
        tone: 'text-red-600 bg-red-50',
    },
    allocation_reactivated: {
        label: 'Reativação',
        icon: ArrowUturnLeftIcon,
        tone: 'text-emerald-600 bg-emerald-50',
    },
    allocation_finished: {
        label: 'Concluída',
        icon: CheckCircleIcon,
        tone: 'text-emerald-600 bg-emerald-50',
    },
    allocation_deleted: {
        label: 'Remoção',
        icon: TrashIcon,
        tone: 'text-red-600 bg-red-50',
    },
    allocation_end_approaching: {
        label: 'Termina em breve',
        icon: BellAlertIcon,
        tone: 'text-amber-600 bg-amber-50',
    },
    allocation_end_overdue: {
        label: 'Prazos vencidos',
        icon: ExclamationTriangleIcon,
        tone: 'text-red-600 bg-red-50',
    },
    collaborator_preview_status: {
        label: 'Status global',
        icon: BoltIcon,
        tone: 'text-indigo-600 bg-indigo-50',
    },
};

const getSeniorityEmoji = (seniorityName?: string | null): string => {
    if (!seniorityName) return '⭐';
    const lower = seniorityName.toLowerCase();
    if (lower.includes('estagi') || lower.includes('trainee')) return '🌱';
    if (lower.includes('junior') || lower.includes('jr')) return '🌿';
    if (lower.includes('pleno') || lower.includes('mid')) return '🌳';
    if (lower.includes('senior') || lower.includes('sr')) return '🌲';
    if (lower.includes('specialist') || lower.includes('especialista')) return '🏆';
    if (lower.includes('lead')) return '👑';
    return '⭐';
};

const buildPrimaryCapabilitySummary = (resources?: Array<Record<string, any>> | null): string | null => {
    if (!resources || resources.length === 0) {
        return null;
    }
    const primaryResource = resources.find((resource) => resource?.isPrimary) ?? resources[0];
    if (!primaryResource) {
        return null;
    }

    const technologyName = primaryResource.technologyName ?? primaryResource.technologyId ?? '';
    const typeName = primaryResource.typeName ?? primaryResource.typeId ?? '';
    const seniorityName = primaryResource.seniorityName ?? primaryResource.seniorityId ?? '';

    const parts = [technologyName, typeName].filter(Boolean);
    const baseLabel = parts.join(' • ');

    const senioritySuffix =
        seniorityName && parts.length > 0
            ? ` (${seniorityName})`
            : seniorityName && parts.length === 0
                ? seniorityName
                : '';

    const coreLabel = (baseLabel || senioritySuffix)?.trim();
    if (!coreLabel) {
        return null;
    }

    const emoji = getSeniorityEmoji(seniorityName);
    return `${emoji} ${coreLabel}`.trim();
};

const SEVERITY_META: Record<Severity, { label: string; color: string; soft: string }> = {
    info: {
        label: 'Informação',
        color: 'bg-sky-500',
        soft: 'text-sky-700 bg-sky-50',
    },
    success: {
        label: 'Sucesso',
        color: 'bg-emerald-500',
        soft: 'text-emerald-700 bg-emerald-50',
    },
    warning: {
        label: 'Alerta',
        color: 'bg-amber-500',
        soft: 'text-amber-700 bg-amber-50',
    },
    danger: {
        label: 'Crítico',
        color: 'bg-red-500',
        soft: 'text-red-700 bg-red-50',
    },
};

function classNames(...classes: Array<string | false | null | undefined>): string {
    return classes.filter(Boolean).join(' ');
}

function formatDate(value: Date | null | undefined, options: Intl.DateTimeFormatOptions): string | null {
    if (!value) return null;
    try {
        return value.toLocaleString('pt-BR', options);
    } catch {
        return value.toISOString();
    }
}

function parseDate(value: unknown): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'string') {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    if (typeof (value as any)?.toDate === 'function') {
        const parsed = (value as { toDate: () => Date }).toDate();
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
}

function formatDateLabel(value: unknown): string | null {
    const parsed = parseDate(value);
    return formatDate(parsed, { dateStyle: 'short' });
}

const DEDICATION_SUFFIX_MAP: Record<string, string> = {
    [AllocationBucketTypes.Defined]: 'horas totais',
    [AllocationBucketTypes.Daily]: 'horas/dia',
    [AllocationBucketTypes.Weekly]: 'horas/semana',
    [AllocationBucketTypes.Monthly]: 'horas/mês',
};

export function formatDedication(bucket: unknown, bucketType: unknown): string {
    if (!bucketType) {
        return 'Sem dedicação';
    }

    const numeric = typeof bucket === 'number'
        ? bucket
        : typeof bucket === 'string'
            ? Number.parseFloat(bucket)
            : Number.NaN;

    if (!Number.isFinite(numeric)) {
        const typeMeta = ALLOCATION_BUCKET_METADATA[String(bucketType) as keyof typeof ALLOCATION_BUCKET_METADATA];
        return typeMeta?.label ?? 'Sem dedicação';
    }

    const formatted = Number.isInteger(numeric)
        ? `${numeric}`
        : numeric.toLocaleString('pt-BR', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 2,
        });

    let suffix: string;
    switch (bucketType) {
        case AllocationBucketTypes.Defined:
            suffix = ' totais';
            break;
        case AllocationBucketTypes.Daily:
            suffix = '/dia';
            break;
        case AllocationBucketTypes.Weekly:
            suffix = '/semana';
            break;
        case AllocationBucketTypes.Monthly:
            suffix = '/mês';
            break;
        default:
            suffix = '';
    }

    const valueWithUnit = `${formatted}h${suffix}`.trim();
    return valueWithUnit || 'Sem dedicação';
}

export interface FormattedEventMessage {
    title: string;
    details: string[];
    clientId: string | null;
    clientName: string | null;
    projectName: string | null;
    capacityLabel: string | null;
    capacityTypeName: string | null;
    capacityTechnologyName: string | null;
    capacitySeniorityName: string | null;
}

function formatEventMessage(event: AllocationTimelineEvent, collaboratorName: string): FormattedEventMessage {
    const actorName = event.actor?.name ?? 'Sistema';
    const payload = event.payload ?? {};
    const clientId = (payload.newClientId ?? payload.clientId ?? null) as string | null;
    const clientName = (payload.newClientName ?? payload.clientName ?? null) as string | null;
    const projectName = (payload.newProjectName ?? payload.projectName ?? null) as string | null;
    const clientLabel = clientName ? `${clientName}${projectName ? ` (${projectName})` : ''}` : null;
    const capacityLabel = formatCapacityLabel(payload);
    const capacityTypeName = (payload.capacityTypeName ?? payload.resourceTypeName ?? null) as string | null;
    const capacityTechnologyName = (payload.capacityTechnologyName ?? payload.technologyName ?? null) as string | null;
    const capacitySeniorityName = (payload.capacitySeniorityName ?? payload.seniorityName ?? null) as string | null;

    const details: Array<string | null> = [];
    const addDetail = (value: string | null) => {
        if (value) details.push(value);
    };

    let title = 'Atualização de alocação';

    switch (event.type) {
        case 'allocation_created':
            addDetail(formatDateLabel(payload.startDate) ? `Início: ${formatDateLabel(payload.startDate)}` : null);
            addDetail(formatDateLabel(payload.endDate) ? `Fim: ${formatDateLabel(payload.endDate)}` : null);
            addDetail(formatDedication(payload.bucket, payload.bucketType));
            if (payload.startDate) {
                const startDate = parseDate(payload.startDate);
                if (startDate) {
                    const startAt = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                    const today = new Date();
                    const todayAt = new Date(today.getFullYear(), today.getMonth(), today.getDate());

                    if (startAt.getTime() > todayAt.getTime()) {
                        title = 'Alocação programada';
                    } else if (startAt.getTime() === todayAt.getTime()) {
                        title = 'Alocação iniciada';
                    } else {
                        title = 'Nova alocação';
                    }
                } else {
                    title = 'Nova alocação';
                }
            } else {
                title = 'Nova alocação';
            }
            break;

        case 'allocation_updated_start':
            addDetail(formatDateLabel(payload.previousStartDate) ? `Anterior: ${formatDateLabel(payload.previousStartDate)}` : null);
            addDetail(formatDateLabel(payload.newStartDate) ? `Nova: ${formatDateLabel(payload.newStartDate)}` : null);
            title = 'Início ajustado';
            break;

        case 'allocation_updated_end':
            addDetail(formatDateLabel(payload.previousEndDate) ? `Anterior: ${formatDateLabel(payload.previousEndDate)}` : null);
            addDetail(formatDateLabel(payload.newEndDate) ? `Nova: ${formatDateLabel(payload.newEndDate)}` : null);
            title = 'Término ajustado';
            break;

        case 'allocation_extended':
            addDetail(formatDateLabel(payload.previousEndDate) ? `Anterior: ${formatDateLabel(payload.previousEndDate)}` : null);
            addDetail(formatDateLabel(payload.newEndDate) ? `Novo fim: ${formatDateLabel(payload.newEndDate)}` : null);
            title = 'Alocação prorrogada';
            break;

        case 'allocation_dedication_changed':
            addDetail(
                formatDedication(payload.previousBucket, payload.previousBucketType)
                    ? `Anterior: ${formatDedication(payload.previousBucket, payload.previousBucketType)}`
                    : null
            );
            addDetail(
                formatDedication(payload.newBucket, payload.newBucketType)
                    ? `Atual: ${formatDedication(payload.newBucket, payload.newBucketType)}`
                    : null
            );
            title = 'Dedicação ajustada';
            break;

        case 'allocation_type_changed':
            addDetail(
                payload.previousType
                    ? `Anterior: ${ALLOCATION_TYPE_METADATA[payload.previousType as keyof typeof ALLOCATION_TYPE_METADATA]?.label ?? payload.previousType}`
                    : null
            );
            addDetail(
                payload.newType
                    ? `Atual: ${ALLOCATION_TYPE_METADATA[payload.newType as keyof typeof ALLOCATION_TYPE_METADATA]?.label ?? payload.newType}`
                    : null
            );
            title = 'Tipo de alocação alterado';
            break;

        case 'allocation_client_changed':
            addDetail(payload.previousClientName ? `Anterior: ${payload.previousClientName}` : null);
            addDetail(payload.newClientName ? `Atual: ${payload.newClientName}` : null);
            addDetail(payload.projectName ? `Projeto: ${payload.projectName}` : null);
            title = 'Cliente alterado';
            break;

        case 'allocation_project_changed':
            addDetail(payload.previousProjectName ? `Anterior: ${payload.previousProjectName}` : null);
            addDetail(payload.newProjectName ? `Atual: ${payload.newProjectName}` : null);
            title = 'Projeto alterado';
            break;

        case 'allocation_responsible_changed':
            addDetail(payload.previousResponsibleName ? `Anterior: ${payload.previousResponsibleName}` : null);
            addDetail(payload.newResponsibleName ? `Atual: ${payload.newResponsibleName}` : null);
            title = 'Responsável interno alterado';
            break;

        case 'allocation_client_responsible_changed':
            addDetail(payload.previousClientResponsible ? `Anterior: ${payload.previousClientResponsible}` : null);
            addDetail(payload.newClientResponsible ? `Atual: ${payload.newClientResponsible}` : null);
            title = 'Contato do cliente alterado';
            break;

        case 'allocation_rate_changed':
            addDetail(payload.previousRate != null ? `Anterior: R$ ${payload.previousRate}` : null);
            addDetail(payload.newRate != null ? `Atual: R$ ${payload.newRate}` : null);
            title = 'Taxa atualizada';
            break;

        case 'allocation_cancelled':
            addDetail(formatDateLabel(payload.canceledAt) ? `Cancelada em: ${formatDateLabel(payload.canceledAt)}` : null);
            addDetail(payload.cancelReason ? `Motivo: ${payload.cancelReason}` : null);
            addDetail(projectName ? `Projeto: ${projectName}` : null);
            title = 'Alocação cancelada';
            break;

        case 'allocation_reactivated':
            title = 'Alocação reativada';
            break;

        case 'allocation_finished':
            addDetail(formatDateLabel(payload.finishedAt) ? `Finalizada em: ${formatDateLabel(payload.finishedAt)}` : null);
            title = 'Alocação concluída';
            break;

        case 'allocation_deleted':
            addDetail(formatDateLabel(payload.startDate) ? `Iniciaria em: ${formatDateLabel(payload.startDate)}` : null);
            title = 'Alocação removida';
            break;

        case 'allocation_end_approaching': {
            const daysUntilEnd = payload.daysUntilEnd as number | null;
            let titleSuffix = 'em breve';
            if (typeof daysUntilEnd === 'number') {
                if (daysUntilEnd === 0) {
                    titleSuffix = 'hoje';
                } else if (daysUntilEnd === 1) {
                    titleSuffix = 'em 1 dia';
                } else {
                    titleSuffix = `em ${daysUntilEnd} dias`;
                }
            }

            addDetail(formatDateLabel(payload.endDate) ? `Fim previsto: ${formatDateLabel(payload.endDate)}` : null);
            title = `Término se aproximando ${titleSuffix}`;
            break;
        }

        case 'allocation_end_overdue':
            addDetail(
                typeof payload.overdueDays === 'number'
                    ? `Atraso: ${payload.overdueDays} dia${payload.overdueDays === 1 ? '' : 's'}`
                    : null
            );
            addDetail(formatDateLabel(payload.endDate) ? `Fim previsto: ${formatDateLabel(payload.endDate)}` : null);
            title = 'Alocação em atraso';
            break;

        case 'collaborator_preview_status':
            title = 'Status global atualizado';
            break;

        default:
            title = 'Registro de alocação atualizado';
            break;
    }

    return {
        title,
        details: details.filter(Boolean) as string[],
        clientId,
        clientName: clientName ?? null,
        projectName: projectName ?? null,
        capacityLabel,
        capacityTypeName,
        capacityTechnologyName,
        capacitySeniorityName,
    };
}

export interface EnrichedEvent {
    event: AllocationTimelineEvent;
    createdAt: Date | null;
    message: FormattedEventMessage;
    actorName: string;
    collaboratorName: string;
    severity: Severity;
    meta: EventMeta;
    vacationLabel?: ReturnType<typeof buildVacationAllocationLabel>;
}

export interface GroupedEvents {
    dateKey: string;
    label: string;
    fullLabel: string;
    events: EnrichedEvent[];
}

export interface ClientAllocationTypeStat {
    type: string;
    label: string;
    count: number;
    icon: ComponentType<{ className?: string }>;
}

export interface ClientInitiativeMember {
    entryId: string;
    collaboratorId: string | null;
    collaboratorName: string;
    collaboratorAvatar?: string | null;
    allocationType?: string | null;
    allocationTypeLabel: string;
    allocationTypeIcon?: ComponentType<{ className?: string }>;
    primaryCapability?: string | null;
    dedication?: string | null;
    startDate: Date | null;
    endDate: Date | null;
    rate?: number | null;
    capacityLabel?: string | null;
    capacityTypeName?: string | null;
    capacityTechnologyName?: string | null;
    capacitySeniorityName?: string | null;
    clientResponsible?: string | null;
    vacationSummary?: CollaboratorVacationSummary;
}

export interface ClientInitiativeData {
    name: string;
    totalEntries: number;
    clientResponsibles: string[];
    members: ClientInitiativeMember[];
}

export interface ClientsViewData {
    clientId: string | null;
    clientName: string;
    clientLogo?: string | null;
    totalPeople: number;
    allocationsByType: ClientAllocationTypeStat[];
    initiatives: ClientInitiativeData[];
}

function formatDayGroup(date: Date | null): { key: string; label: string; full: string } {
    if (!date) {
        return { key: 'sem-data', label: 'Sem data', full: 'Data não informada' };
    }

    const today = new Date();
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const diffDays = Math.round((todayStart.getTime() - dayStart.getTime()) / (1000 * 60 * 60 * 24));

    let label = date.toLocaleDateString('pt-BR', { weekday: 'long' });
    label = label.charAt(0).toUpperCase() + label.slice(1);

    if (diffDays === 0) {
        label = 'Hoje';
    } else if (diffDays === 1) {
        label = 'Ontem';
    }

    const full = date.toLocaleDateString('pt-BR', { dateStyle: 'full' });
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    return { key, label, full };
}

interface CollaboratorMapEntry {
    name: string;
    photoUrl?: string | null;
    primaryCapability?: string | null;
    vacationSummary?: CollaboratorVacationSummary;
}

function useCollaboratorsMap(enabled: boolean) {
    const { data: collaborators } = useCollaboratorsRealtimeQuery({ enabled });

    return useMemo(() => {
        const map = new Map<string, CollaboratorMapEntry>();
        collaborators.forEach((collaborator) => {
            if (collaborator.id) {
                map.set(collaborator.id, {
                    name: collaborator.name,
                    photoUrl: collaborator.photoUrl,
                    primaryCapability: buildPrimaryCapabilitySummary(collaborator.resources ?? null),
                    vacationSummary: (collaborator as any).vacationSummary,
                });
            }
        });
        return map;
    }, [collaborators]);
}

export default function CollaboratorAllocationsOverviewPage() {
    const navigate = useNavigate();
    const {
        collaborators: layoutCollaborators = [],
        isLoading: layoutIsLoading = false,
    } = useOutletContext<CollaboratorsLayoutContext>();

    // Check permissions for realtime query
    const { userHasRole } = useAuth();
    const hasAccess = userHasRole(ROLE_ADMIN) ||
        userHasRole(ROLE_COLLABORATOR_ADMIN) ||
        userHasRole(ROLE_COLLABORATOR_VIEWER) ||
        userHasRole(ROLE_COLLABORATOR_EDITOR);

    const [viewMode, setViewMode] = useState<ViewMode>('timeline');
    const [collaboratorFilter, setCollaboratorFilter] = useState('');
    const [selectedSeverity, setSelectedSeverity] = useState<Severity | 'all'>('all');
    const [selectedType, setSelectedType] = useState<EventType | 'all'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const PAGE_SIZE = 20;
    const [pageCount, setPageCount] = useState(1);
    const visibleCount = pageCount * PAGE_SIZE;
    const queryLimit = visibleCount + 1;

    const collaboratorsMap = useCollaboratorsMap(hasAccess);

    const allocationScopeStats = useMemo<AllocationScopeStats>(() => {
        const INACTIVE_STATUSES = new Set(['inactive', 'inativo', 'desligado']);

        const filteredCollaborators = layoutCollaborators.filter((collaborator) => {
            const statusNormalized = (collaborator.status ?? '').toLowerCase();
            if (INACTIVE_STATUSES.has(statusNormalized)) {
                return false;
            }
            return true;
        });

        const total = filteredCollaborators.length;

        const statusCollaborators = new Map<AllocationPreviewStatus, ScopeCollaborator[]>();

        filteredCollaborators.forEach((collaborator) => {
            const status = collaborator.allocationPreview?.status ?? null;
            if (!status) {
                return;
            }

            if (!statusCollaborators.has(status)) {
                statusCollaborators.set(status, []);
            }

            statusCollaborators.get(status)!.push({
                id: collaborator.id ?? collaborator.name ?? `collab-${statusCollaborators.get(status)!.length}`,
                name: collaborator.name ?? 'Colaborador',
                photoUrl: collaborator.photoUrl ?? null,
            });
        });

        const baseStats: ScopeStat[] = ALLOCATION_STATUS_ORDER.map((status) => {
            const collaboratorsForStatus = statusCollaborators.get(status) ?? [];
            const matching = collaboratorsForStatus.length;
            const percentage = total > 0 ? Math.round((matching / total) * 100) : 0;

            return {
                name: ALLOCATION_STATUS_METADATA[status].title,
                field: status,
                stat: `${percentage}%`,
                updated: matching,
                total,
                filterState: 'none' as const,
                collaborators: collaboratorsForStatus,
            };
        });

        const primaryStats = baseStats
            .filter((stat) => stat.field !== 'Absence')
            .slice(0, 4);
        const absenceStat =
            baseStats.find((stat) => stat.field === 'Absence') ??
            {
                name: ALLOCATION_STATUS_METADATA.Absence.title,
                field: 'Absence',
                stat: '0%',
                updated: 0,
                total,
                filterState: 'none' as const,
                collaborators: [],
            };

        const noticeCollaborators: ScopeCollaborator[] = filteredCollaborators
            .filter((collaborator) => {
                if (collaborator.hasNotice === true) {
                    return true;
                }

                if (collaborator.hasNotice === false) {
                    return false;
                }

                const activeAllocations = collaborator.allocationSummary?.active ?? [];
                return activeAllocations.some(
                    (entry) => entry?.allocationType === AllocationTypes.Product
                );
            })
            .map((collaborator) => ({
                id: collaborator.id ?? collaborator.name ?? `notice-${Math.random()}`,
                name: collaborator.name ?? 'Colaborador',
                photoUrl: collaborator.photoUrl ?? null,
            }));

        return {
            primary: primaryStats,
            absence: absenceStat,
            notice: {
                name: 'Aviso Prévio',
                count: noticeCollaborators.length,
                collaborators: noticeCollaborators,
            },
        };
    }, [layoutCollaborators]);

    const handleAllocationScopeClick = useCallback(
        (field: string) => {
            const status = field as AllocationPreviewStatus;
            const filters = [
                {
                    id: `allocation-status-${status}`,
                    columnId: 'allocationPreview.status',
                    operator: 'equals' as const,
                    value: status,
                    type: 'select' as const,
                    label: 'Status de Alocação',
                    enabled: true,
                },
            ];
            const serialized = btoa(JSON.stringify(filters));
            navigate(`/collaborators/updates?filters=${encodeURIComponent(serialized)}`);
        },
        [navigate]
    );

    const handleNoticeCardClick = useCallback(() => {
        const filters = [
            {
                id: 'allocation-notice',
                columnId: 'hasNotice',
                operator: 'equals' as const,
                value: 'true',
                type: 'boolean' as const,
                label: 'Aviso Prévio',
                enabled: true,
            },
        ];
        const serialized = btoa(JSON.stringify(filters));
        navigate(`/collaborators/updates?filters=${encodeURIComponent(serialized)}`);
    }, [navigate]);

    const viewButtonClass = (mode: ViewMode) =>
        classNames(
            'inline-flex items-center gap-2 rounded-md px-3.5 py-2 text-sm font-medium shadow-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
            viewMode === mode
                ? 'border border-indigo-600 bg-indigo-600 text-white hover:border-indigo-600 hover:text-white'
                : 'border border-gray-200 bg-white text-gray-600 hover:border-indigo-400 hover:text-indigo-600'
        );

    const clientsViewData = useMemo<ClientsViewData[]>(() => {
        const clientsMap = new Map<
            string,
            {
                clientId: string | null;
                clientName: string;
                clientLogo?: string | null;
                collaboratorIds: Set<string>;
                allocationTypeCounts: Map<string, number>;
                initiativesMap: Map<
                    string,
                    {
                        name: string;
                        entries: ClientInitiativeMember[];
                        clientResponsibles: Set<string>;
                    }
                >;
            }
        >();

        layoutCollaborators.forEach((collaborator) => {
            const activeAllocations = collaborator.allocationSummary?.active ?? [];
            activeAllocations.forEach((entry, index) => {
                const clientName = entry.clientName?.trim() || 'Sem cliente definido';
                const normalizedClientKey = (entry.clientId ?? clientName).toLowerCase();
                if (!clientsMap.has(normalizedClientKey)) {
                    clientsMap.set(normalizedClientKey, {
                        clientId: entry.clientId ?? null,
                        clientName,
                        clientLogo: buildClientLogoUrl(entry.clientLogo ?? null),
                        collaboratorIds: new Set(),
                        allocationTypeCounts: new Map(),
                        initiativesMap: new Map(),
                    });
                }

                const client = clientsMap.get(normalizedClientKey)!;
                if (!client.clientLogo && entry.clientLogo) {
                    client.clientLogo = buildClientLogoUrl(entry.clientLogo);
                }

                const collaboratorKey = collaborator.id ?? collaborator.name ?? `colaborador-${client.collaboratorIds.size}`;
                client.collaboratorIds.add(collaboratorKey);

                const allocationTypeKey = entry.allocationType ?? 'Sem tipo';
                client.allocationTypeCounts.set(
                    allocationTypeKey,
                    (client.allocationTypeCounts.get(allocationTypeKey) ?? 0) + 1
                );

                const initiativeName = entry.projectName?.trim() || 'Sem iniciativa definida';
                const initiativeKey = initiativeName.toLowerCase();
                if (!client.initiativesMap.has(initiativeKey)) {
                    client.initiativesMap.set(initiativeKey, {
                        name: initiativeName,
                        entries: [],
                        clientResponsibles: new Set(),
                    });
                }

                const initiative = client.initiativesMap.get(initiativeKey)!;
                if (entry.clientResponsible) {
                    initiative.clientResponsibles.add(entry.clientResponsible);
                }

                const allocationTypeLabel =
                    entry.allocationType && ALLOCATION_TYPE_METADATA[entry.allocationType as keyof typeof ALLOCATION_TYPE_METADATA]
                        ? ALLOCATION_TYPE_METADATA[entry.allocationType as keyof typeof ALLOCATION_TYPE_METADATA].label
                        : entry.allocationType ?? 'Sem tipo definido';
                const AllocationTypeIcon =
                    entry.allocationType && ALLOCATION_TYPE_ICON_MAP[entry.allocationType as keyof typeof ALLOCATION_TYPE_ICON_MAP]
                        ? ALLOCATION_TYPE_ICON_MAP[entry.allocationType as keyof typeof ALLOCATION_TYPE_ICON_MAP]
                        : DefaultTypeIcon;

                const entryId =
                    entry.id ??
                    `${collaborator.id ?? 'colab'}-${normalizedClientKey}-${initiativeKey}-${index}`;

                const dedicationText = formatDedication(entry.bucket ?? null, entry.bucketType ?? null);
                const normalizedDedication = dedicationText === 'Sem dedicação' ? null : dedicationText;
                initiative.entries.push({
                    entryId,
                    collaboratorId: collaborator.id ?? null,
                    collaboratorName: collaborator.name ?? 'Colaborador',
                    collaboratorAvatar: collaborator.photoUrl ?? null,
                    allocationType: entry.allocationType ?? null,
                    allocationTypeLabel,
                    allocationTypeIcon: AllocationTypeIcon,
                    dedication: normalizedDedication,
                    startDate: parseDate(entry.startDate),
                    endDate: parseDate(entry.endDate),
                    rate:
                        typeof entry.rate === 'number'
                            ? entry.rate
                            : entry.rate != null
                                ? Number(entry.rate)
                                : null,
                    capacityLabel: entry.capacityLabel ?? null,
                    capacityTypeName: entry.capacityTypeName ?? null,
                    capacityTechnologyName: entry.capacityTechnologyName ?? null,
                    capacitySeniorityName: entry.capacitySeniorityName ?? null,
                    clientResponsible: entry.clientResponsible ?? null,
                    primaryCapability: buildPrimaryCapabilitySummary((collaborator as any)?.resources ?? null),
                    vacationSummary: (collaborator as any).vacationSummary,
                });
            });
        });

        const clientsList = Array.from(clientsMap.values())
            .map((client) => {
                const allocationsByType: ClientAllocationTypeStat[] = Array.from(
                    client.allocationTypeCounts.entries()
                )
                    .map(([type, count]) => {
                        const allocationLabel =
                            ALLOCATION_TYPE_METADATA[type as keyof typeof ALLOCATION_TYPE_METADATA]?.label ?? type;
                        const IconComponent =
                            ALLOCATION_TYPE_ICON_MAP[type as keyof typeof ALLOCATION_TYPE_ICON_MAP] ?? DefaultTypeIcon;
                        return {
                            type,
                            label: allocationLabel,
                            count,
                            icon: IconComponent,
                        };
                    })
                    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

                const initiatives: ClientInitiativeData[] = Array.from(client.initiativesMap.values())
                    .map((initiative) => ({
                        name: initiative.name,
                        totalEntries: initiative.entries.length,
                        clientResponsibles: Array.from(initiative.clientResponsibles).sort((a, b) =>
                            a.localeCompare(b, 'pt-BR')
                        ),
                        members: initiative.entries.sort((a, b) =>
                            a.collaboratorName.localeCompare(b.collaboratorName, 'pt-BR')
                        ),
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

                return {
                    clientId: client.clientId,
                    clientName: client.clientName,
                    clientLogo: buildClientLogoUrl(client.clientLogo) ?? null,
                    totalPeople: client.collaboratorIds.size,
                    allocationsByType,
                    initiatives,
                };
            })
            .sort((a, b) => a.clientName.localeCompare(b.clientName, 'pt-BR'));

        return clientsList;
    }, [layoutCollaborators]);

    const timelineQuery = useAllocationTimelineQuery({
        collaboratorId: collaboratorFilter || undefined,
        limit: queryLimit,
    });

    const enrichedEvents: EnrichedEvent[] = useMemo(() => {
        return timelineQuery.data.map((event) => {
            const collaboratorName = event.collaborator?.name
                ?? collaboratorsMap.get(event.collaboratorId)?.name
                ?? 'Colaborador';
            const message = formatEventMessage(event, collaboratorName);
            const createdAt = event.createdAt
                ? event.createdAt instanceof Date
                    ? event.createdAt
                    : event.createdAt.toDate()
                : null;
            const actorName = event.actor?.name ?? 'Sistema';
            const severity = (event.severity ?? 'info') as Severity;
            const meta = EVENT_TYPE_META[event.type] ?? {
                label: 'Evento',
                icon: InformationCircleIcon,
                tone: 'text-gray-600 bg-gray-100',
            };

            const vacationSummary = collaboratorsMap.get(event.collaboratorId)?.vacationSummary;
            const vacationLabel = buildVacationAllocationLabel(vacationSummary, {
                allocationId: event.allocationId,
            });

            return {
                event,
                createdAt,
                message,
                actorName,
                collaboratorName,
                severity,
                meta,
                vacationLabel,
            };
        });
    }, [timelineQuery.data, collaboratorsMap]);

    const searchTermNormalized = normalizeString(searchTerm);

    const filteredEvents = useMemo(() => {
        return enrichedEvents.filter(({ event, message, collaboratorName, actorName }) => {
            if (selectedSeverity !== 'all' && event.severity !== selectedSeverity) {
                return false;
            }

            if (selectedType !== 'all' && event.type !== selectedType) {
                return false;
            }

            if (!searchTermNormalized) {
                return true;
            }

            const haystack = [
                message.title,
                collaboratorName,
                actorName,
                JSON.stringify(event.payload ?? {}),
            ].join(' ');

            return normalizeString(haystack).includes(searchTermNormalized);
        });
    }, [enrichedEvents, selectedSeverity, selectedType, searchTermNormalized]);

    useEffect(() => {
        setPageCount(1);
    }, [collaboratorFilter, selectedSeverity, selectedType, searchTermNormalized]);

    const displayedEvents = useMemo(() => {
        return filteredEvents.slice(0, visibleCount);
    }, [filteredEvents, visibleCount]);

    const groupedEvents: GroupedEvents[] = useMemo(() => {
        const groupsMap = new Map<string, GroupedEvents>();

        displayedEvents.forEach((item) => {
            const { key, label, full } = formatDayGroup(item.createdAt);
            if (!groupsMap.has(key)) {
                groupsMap.set(key, { dateKey: key, label, fullLabel: full, events: [] });
            }
            groupsMap.get(key)!.events.push(item);
        });

        const groups = Array.from(groupsMap.values());
        groups.sort((a, b) => (a.dateKey < b.dateKey ? 1 : -1));
        groups.forEach((group) => {
            group.events.sort((a, b) => {
                const timeA = a.createdAt?.getTime?.() ?? 0;
                const timeB = b.createdAt?.getTime?.() ?? 0;
                return timeB - timeA;
            });
        });
        return groups;
    }, [displayedEvents]);

    const hasEvents = displayedEvents.length > 0;
    const hasMore = timelineQuery.data.length > visibleCount;
    const handleLoadMore = () => {
        if (!timelineQuery.isFetching) {
            setPageCount((count) => count + 1);
        }
    };
    const needsCollaborators = timelineQuery.data.length > 0;
    const isInitialLoading =
        (timelineQuery.isLoading && timelineQuery.data.length === 0) ||
        (hasAccess && needsCollaborators && collaboratorsMap.size === 0);

    return (
        <div className="space-y-8">
            <section>
                {layoutIsLoading ? (
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
                        {Array.from({ length: 4 }).map((_, index) => (
                            <div
                                key={`primary-skeleton-${index}`}
                                className="relative overflow-hidden rounded-lg bg-white px-4 pt-5 pb-12 shadow sm:px-6 sm:pt-6"
                            >
                                <div className="animate-pulse space-y-4">
                                    <div className="flex items-start justify-between">
                                        <div className="h-4 w-24 rounded bg-gray-100" />
                                        <div className="h-5 w-5 rounded-full bg-gray-100" />
                                    </div>
                                    <div className="h-7 w-20 rounded bg-gray-100" />
                                    <div className="absolute inset-x-0 bottom-0 bg-gray-50 px-4 py-4 sm:px-6">
                                        <div className="h-4 w-32 rounded bg-gray-100" />
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div className="grid gap-5">
                            {Array.from({ length: 2 }).map((_, index) => (
                                <div
                                    key={`secondary-skeleton-${index}`}
                                    className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:px-6"
                                >
                                    <div className="animate-pulse space-y-3">
                                        <div className="h-4 w-28 rounded bg-gray-100" />
                                        <div className="h-7 w-20 rounded bg-gray-100" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
                        {allocationScopeStats.primary.map((item) => {
                            const borderClass =
                                item.filterState === 'not_updated' ? 'border-2 border-yellow-500' :
                                    item.filterState === 'updated' ? 'border-2 border-green-500' :
                                        'border border-transparent';
                            const collaborators = item.collaborators ?? [];
                            const displayCollaborators = collaborators.slice(0, AVATAR_DISPLAY_LIMIT);
                            const extraCollaborators = Math.max(collaborators.length - displayCollaborators.length, 0);

                            return (
                                <div
                                    key={item.name}
                                    onClick={() => handleAllocationScopeClick(item.field)}
                                    className={`group relative cursor-pointer overflow-hidden rounded-lg bg-white px-4 pt-5 pb-12 shadow transition hover:shadow-lg sm:px-6 sm:pt-6 ${borderClass}`}
                                >
                                    <dt>
                                        <div className="flex items-start justify-between">
                                            <p className="truncate text-sm font-medium text-gray-500">{item.name}</p>
                                            {item.filterState === 'not_updated' && <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />}
                                            {item.filterState === 'updated' && <CheckCircleIcon className="h-5 w-5 text-green-500" />}
                                        </div>
                                    </dt>
                                    <dd className="flex items-baseline pb-6 sm:pb-7">
                                        <p className="text-2xl font-semibold text-gray-900">{item.stat}</p>
                                    </dd>
                                    {displayCollaborators.length > 0 && (
                                        <div className="mt-2 flex items-center pb-6 sm:pb-7">
                                            <div className="flex flex-wrap items-center gap-2">
                                                {displayCollaborators.map((collaborator) => (
                                                    <CollaboratorAvatar
                                                        key={`${item.field}-${collaborator.id}`}
                                                        photoUrl={collaborator.photoUrl ?? undefined}
                                                        name={collaborator.name}
                                                        size="xsmall"
                                                    />
                                                ))}
                                                {extraCollaborators > 0 && (
                                                    <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-gray-200 px-1 text-[11px] font-semibold text-gray-600">
                                                        +{extraCollaborators}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <div className="absolute inset-x-0 bottom-0 bg-gray-50 px-4 py-4 transition group-hover:bg-gray-100 sm:px-6">
                                        <div className="text-sm">
                                            <p className="font-medium text-gray-600">
                                                {item.updated} de {item.total} colaboradores
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        <div className="grid gap-5">
                            <div
                                onClick={() => handleAllocationScopeClick(allocationScopeStats.absence.field)}
                                className="group cursor-pointer overflow-hidden rounded-lg bg-white px-4 py-5 shadow transition hover:shadow-lg sm:px-6"
                            >
                                <div className="flex items-start">
                                    <p className="truncate text-sm font-medium text-gray-500">
                                        {allocationScopeStats.absence.name}
                                    </p>
                                </div>
                                <p className="mt-4 text-2xl font-semibold text-gray-900">
                                    {allocationScopeStats.absence.stat}
                                </p>
                                {allocationScopeStats.absence.collaborators.length > 0 && (
                                    <div className="mt-3 flex items-center">
                                        <div className="flex flex-wrap items-center gap-2">
                                            {allocationScopeStats.absence.collaborators.slice(0, AVATAR_DISPLAY_LIMIT).map((collaborator) => (
                                                <CollaboratorAvatar
                                                    key={`absence-${collaborator.id}`}
                                                    photoUrl={collaborator.photoUrl ?? undefined}
                                                    name={collaborator.name}
                                                    size="xsmall"
                                                />
                                            ))}
                                            {allocationScopeStats.absence.collaborators.length > AVATAR_DISPLAY_LIMIT && (
                                                <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-gray-200 px-1 text-[11px] font-semibold text-gray-600">
                                                    +{allocationScopeStats.absence.collaborators.length - AVATAR_DISPLAY_LIMIT}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div
                                onClick={handleNoticeCardClick}
                                className="group cursor-pointer overflow-hidden rounded-lg bg-white px-4 py-5 shadow transition hover:shadow-lg sm:px-6"
                            >
                                <div className="flex items-start">
                                    <p className="truncate text-sm font-medium text-gray-500">
                                        {allocationScopeStats.notice.name}
                                    </p>
                                </div>
                                <p className="mt-4 text-2xl font-semibold text-gray-900">
                                    {allocationScopeStats.notice.count}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </section>

            <section className="bg-white shadow rounded-lg p-4 sm:p-5 space-y-4">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    <div className="flex flex-col gap-2">
                        <span className="text-xs font-semibold text-gray-500">Colaborador</span>
                        <div className="-mt-1">
                            <CollaboratorCombobox
                                selectedId={collaboratorFilter || null}
                                onChange={(collaborator) => setCollaboratorFilter(collaborator?.id ?? '')}
                                placeholder="Filtrar colaborador"
                            />
                        </div>
                    </div>

                    <label className="flex flex-col gap-2">
                        <span className="text-xs font-semibold text-gray-500">Pesquisar</span>
                        <div className="relative">
                            <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                placeholder="Cliente, projeto, responsável..."
                                className="w-full rounded-md border border-gray-300 bg-white py-2 pl-8 pr-3 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                    </label>

                    <div className="flex flex-col gap-2">
                        <span className="text-xs font-semibold text-gray-500">Tipo de evento</span>
                        <div className="relative">
                            <select
                                value={selectedType}
                                onChange={(event) => setSelectedType(event.target.value as EventType | 'all')}
                                className="w-full appearance-none rounded-md border border-gray-300 bg-white py-2 pl-3 pr-9 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="all">Todos os tipos</option>
                                {Object.entries(EVENT_TYPE_META).map(([key, meta]) => (
                                    <option key={key} value={key}>
                                        {meta.label}
                                    </option>
                                ))}
                            </select>
                            <AdjustmentsHorizontalIcon className="pointer-events-none absolute right-2 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                        </div>
                    </div>

                    <div className="md:col-span-2 xl:col-span-3">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
                            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
                                <span className="text-xs font-semibold text-gray-500 lg:whitespace-nowrap">Severidade</span>
                                <div className="flex flex-wrap gap-2">
                                    {(['all', 'danger', 'warning', 'success', 'info'] as Array<Severity | 'all'>).map((value) => {
                                        const meta = value !== 'all' ? SEVERITY_META[value] : null;
                                        const isActive = selectedSeverity === value;
                                        return (
                                            <button
                                                key={value}
                                                type="button"
                                                onClick={() => setSelectedSeverity(value)}
                                                className={classNames(
                                                    'rounded-full border px-3 py-1 text-xs font-medium transition',
                                                    isActive
                                                        ? value === 'all'
                                                            ? 'bg-gray-900 text-white border-gray-900'
                                                            : `${meta?.soft ?? 'text-gray-700 bg-gray-100'} border-transparent`
                                                        : 'border-gray-200 text-gray-500 hover:border-gray-400'
                                                )}
                                            >
                                                {value === 'all' ? 'Todas' : meta?.label ?? value}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setViewMode('timeline')}
                                    className={viewButtonClass('timeline')}
                                    aria-pressed={viewMode === 'timeline'}
                                >
                                    <ClockIcon className="h-4 w-4" />
                                    Timeline
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode('clients')}
                                    className={viewButtonClass('clients')}
                                    aria-pressed={viewMode === 'clients'}
                                >
                                    <BuildingOfficeIcon className="h-4 w-4" />
                                    Clientes
                                </button>
                            </div>
                            <div className="mt-2 flex lg:mt-0 lg:items-center">
                                <Link
                                    to="/collaborators/allocations/new"
                                    className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                >
                                    <PlusIcon className="h-5 w-5" />
                                    Registrar alocação
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {viewMode === 'timeline' ? (
                <TimelineView
                    groups={groupedEvents}
                    isInitialLoading={isInitialLoading}
                    hasEvents={hasEvents}
                    hasMore={hasMore}
                    onLoadMore={handleLoadMore}
                    isLoadingMore={timelineQuery.isFetching}
                    collaboratorsMap={collaboratorsMap}
                    severityMetaMap={SEVERITY_META}
                />
            ) : (
                <ClientsView
                    data={clientsViewData}
                    isLoading={layoutIsLoading}
                />
            )}
        </div>
    );
}
